let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  FlatList,
  Alert,
  ActivityIndicator,
  Dimensions,
  Modal,
  Switch,
  RefreshControl,
  Animated,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons, MaterialCommunityIcons, FontAwesome5 } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// Safe service import with fallback
let UnifiedIntegrationService = null;
try {
  const serviceModule = require('../../services/UnifiedIntegrationService');
  UnifiedIntegrationService = serviceModule.default || serviceModule;
} catch (error) {
  console.warn('UnifiedIntegrationService not found, using mock implementation');
}

// Mock service implementation
const MockUnifiedIntegrationService = {
  initialize: async () => {
    console.log('Mock UnifiedIntegrationService initialized');
    return true;
  },
  getNotifications: async () => {
    console.log('Mock getNotifications called');
    return [];
  },
  markNotificationAsRead: async (id) => {
    console.log('Mock markNotificationAsRead called:', id);
    return true;
  },
  markAllNotificationsAsRead: async () => {
    console.log('Mock markAllNotificationsAsRead called');
    return true;
  },
  updateNotificationSettings: async (settings) => {
    console.log('Mock updateNotificationSettings called:', settings);
    return true;
  },
  getNotificationSettings: async () => {
    console.log('Mock getNotificationSettings called');
    return {};
  },
  createNotification: async (notification) => {
    console.log('Mock createNotification called:', notification);
    return true;
  },
  deleteNotification: async (id) => {
    console.log('Mock deleteNotification called:', id);
    return true;
  },
  syncNotifications: async () => {
    console.log('Mock syncNotifications called');
    return true;
  },
  createJobAlert: async (alert) => {
    console.log('Mock createJobAlert called:', alert);
    return true;
  },
  updateJobAlert: async (alert) => {
    console.log('Mock updateJobAlert called:', alert);
    return true;
  },
  deleteJobAlert: async (id) => {
    console.log('Mock deleteJobAlert called:', id);
    return true;
  },
  getJobAlerts: async () => {
    console.log('Mock getJobAlerts called');
    return [];
  },
  triggerJobAlert: async (id) => {
    console.log('Mock triggerJobAlert called:', id);
    return true;
  },
};

// Use the actual service if available, otherwise use mock
const IntegrationService = UnifiedIntegrationService || MockUnifiedIntegrationService;

const { width, height } = Dimensions.get('window');

export default function JobAlertsScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_jobalertsscreen`;
    apiFetch('/api/data/jobs?is_active=true&sort=created_at&order=desc&limit=20').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  const navigation = useNavigation();
  
  // State Management
  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [activeTab, setActiveTab] = useState('alerts'); // alerts, notifications, settings
  const [notificationFilter, setNotificationFilter] = useState('all');
  const [fadeAnim] = useState(new Animated.Value(0));

  // Create/Edit Alert Form State
  const [alertForm, setAlertForm] = useState({
    name: '',
    keywords: '',
    location: '',
    jobType: 'all',
    experienceLevel: 'all',
    salaryMin: '',
    salaryMax: '',
    industry: 'all',
    companySize: 'all',
    frequency: 'daily',
    enabled: true,
    emailNotifications: true,
    pushNotifications: true,
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    applications: true,
    interviews: true,
    messages: true,
    jobAlerts: true,
    offers: true,
    assessments: true,
    networkUpdates: false,
    systemUpdates: true,
    pushNotifications: true,
    emailNotifications: true,
    smsNotifications: false,
  });

  // Alert Statistics
  const [alertStats, setAlertStats] = useState({
    totalAlerts: 0,
    activeAlerts: 0,
    jobsFound: 0,
    applicationsFromAlerts: 0,
    notificationsToday: 0,
    unreadNotifications: 0,
  });

  useEffect(() => {
    initializeScreen();
    startAnimations();
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      loadNotifications();
      return () => {
        // Cleanup if needed
      };
    }, [])
  );

  const startAnimations = () => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 600,
      useNativeDriver: true,
    }).start();
  };

  const initializeScreen = async () => {
    try {
      setIsLoading(true);
      
      // Initialize service safely
      if (IntegrationService && typeof IntegrationService.initialize === 'function') {
        await IntegrationService.initialize();
      }
      
      await Promise.all([
        loadJobAlerts(),
        loadNotifications(),
        loadNotificationSettings(),
      ]);
      
      console.log('✅ Job alerts screen initialized successfully');
      
    } catch (error) {
      console.error('Error initializing screen:', error);
      // Don't show error to user, just log it and continue with local data
    } finally {
      setIsLoading(false);
    }
  };

  const loadJobAlerts = async () => {
    try {
      // Try to load from service first
      let serviceAlerts = [];
      if (IntegrationService && typeof IntegrationService.getJobAlerts === 'function') {
        try {
          serviceAlerts = await IntegrationService.getJobAlerts();
        } catch (error) {
          console.warn('Service getJobAlerts failed, using local data:', error);
        }
      }
      
      // Load from AsyncStorage
      const storedAlerts = await AsyncStorage.getItem(`professional_${_profUserId}_jobAlerts`);
      const localAlerts = storedAlerts ? JSON.parse(storedAlerts) : [];
      
      // Create sample data if no alerts exist
      const sampleAlerts = [
        {
          id: 'alert_1',
          name: 'React Native Developer Jobs',
          keywords: 'React Native, Mobile Development, JavaScript',
          location: 'London, UK',
          jobType: 'full-time',
          experienceLevel: 'mid-level',
          salaryMin: '45000',
          salaryMax: '75000',
          industry: 'technology',
          companySize: 'all',
          frequency: 'daily',
          enabled: true,
          emailNotifications: true,
          pushNotifications: true,
          createdAt: '2024-01-15',
          lastTriggered: '2024-01-20',
          jobsFound: 12,
          newJobsToday: 3,
          notificationsGenerated: 8,
        },
        {
          id: 'alert_2',
          name: 'Remote Software Engineer',
          keywords: 'Software Engineer, Remote, Full Stack',
          location: 'Remote',
          jobType: 'remote',
          experienceLevel: 'senior',
          salaryMin: '60000',
          salaryMax: '100000',
          industry: 'technology',
          companySize: 'all',
          frequency: 'instant',
          enabled: true,
          emailNotifications: true,
          pushNotifications: false,
          createdAt: '2024-01-10',
          lastTriggered: '2024-01-20',
          jobsFound: 8,
          newJobsToday: 1,
          notificationsGenerated: 5,
        },
        {
          id: 'alert_3',
          name: 'Part-time Design Jobs',
          keywords: 'UI/UX Designer, Graphic Design, Creative',
          location: 'Manchester, UK',
          jobType: 'part-time',
          experienceLevel: 'all',
          salaryMin: '25000',
          salaryMax: '45000',
          industry: 'design',
          companySize: 'small',
          frequency: 'weekly',
          enabled: false,
          emailNotifications: true,
          pushNotifications: true,
          createdAt: '2024-01-05',
          lastTriggered: '2024-01-18',
          jobsFound: 5,
          newJobsToday: 0,
          notificationsGenerated: 2,
        },
      ];

      // Combine all alerts, prioritizing service data, then local, then sample
      const allAlerts = [...serviceAlerts, ...localAlerts];
      const finalAlerts = allAlerts.length > 0 ? allAlerts : sampleAlerts;
      
      // Remove duplicates
      const uniqueAlerts = finalAlerts.filter((alert, index, self) => 
        index === self.findIndex(a => a.id === alert.id)
      );

      setAlerts(uniqueAlerts);
      
      // Save to AsyncStorage
      await AsyncStorage.setItem(`professional_${_profUserId}_jobAlerts`, JSON.stringify(uniqueAlerts));
      
      // Update statistics
      updateAlertStatistics(uniqueAlerts);
      
    } catch (error) {
      console.error('Error loading job alerts:', error);
      // Don't show alert to user, just use empty array
      setAlerts([]);
    }
  };

  const loadNotifications = async () => {
    try {
      // Try to load from service first
      let serviceNotifications = [];
      if (IntegrationService && typeof IntegrationService.getNotifications === 'function') {
        try {
          serviceNotifications = await IntegrationService.getNotifications();
        } catch (error) {
          console.warn('Service getNotifications failed, using local data:', error);
        }
      }
      
      // Load from AsyncStorage
      const storedNotifications = await AsyncStorage.getItem(`professional_${_profUserId}_notifications`);
      const localNotifications = storedNotifications ? JSON.parse(storedNotifications) : [];
      
      // Generate job alert notifications
      const alertNotifications = generateJobAlertNotifications();
      
      // Generate sample notifications if none exist
      const sampleNotifications = generateSampleNotifications();
      
      // Combine all notifications
      const allNotifications = [
        ...serviceNotifications,
        ...localNotifications,
        ...alertNotifications,
        ...sampleNotifications,
      ];
      
      // Remove duplicates and sort by timestamp
      const uniqueNotifications = allNotifications
        .filter((notification, index, self) => 
          index === self.findIndex(n => n.id === notification.id)
        )
        .sort((a, b) => new Date(b.timestamp || b.date) - new Date(a.timestamp || a.date));
      
      setNotifications(uniqueNotifications);
      filterNotifications(uniqueNotifications, notificationFilter);
      
      // Save to AsyncStorage
      await AsyncStorage.setItem(`professional_${_profUserId}_notifications`, JSON.stringify(uniqueNotifications));
      
    } catch (error) {
      console.error('Error loading notifications:', error);
      // Don't show error to user, just use empty array
      setNotifications([]);
    }
  };

  const generateJobAlertNotifications = () => {
    const alertNotifications = [];
    
    alerts.forEach(alert => {
      if (alert.enabled && alert.newJobsToday > 0) {
        alertNotifications.push({
          id: `alert_notification_${alert.id}_${Date.now()}`,
          type: 'jobAlert',
          category: 'jobAlert',
          title: `New ${alert.name} Matches`,
          message: `${alert.newJobsToday} new jobs matching "${alert.keywords}" in ${alert.location}`,
          timestamp: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString(),
          isRead: Math.random() > 0.5,
          actionable: true,
          actionText: 'View Jobs',
          actionRoute: 'FindJobs',
          actionParams: { alertId: alert.id, keywords: alert.keywords },
          priority: alert.frequency === 'instant' ? 'high' : 'medium',
          alertId: alert.id,
          alertName: alert.name,
        });
      }
    });
    
    return alertNotifications;
  };

  const generateSampleNotifications = () => {
    return [
      {
        id: 'sample_1',
        type: 'jobAlert',
        category: 'jobAlert',
        title: 'New React Native Jobs',
        message: '5 new React Native Developer positions in London match your profile.',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        isRead: false,
        actionable: true,
        actionText: 'View Jobs',
        actionRoute: 'FindJobs',
        actionParams: { keywords: 'React Native' },
        priority: 'medium',
      },
      {
        id: 'sample_2',
        type: 'system',
        category: 'system',
        title: 'Job Alert Created',
        message: 'Your new job alert "Remote Software Engineer" is now active.',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
        isRead: true,
        actionable: false,
        priority: 'low',
      },
      {
        id: 'sample_3',
        type: 'jobAlert',
        category: 'jobAlert',
        title: 'Weekly Job Summary',
        message: 'This week we found 23 new jobs matching your alerts.',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
        isRead: false,
        actionable: true,
        actionText: 'View Summary',
        actionRoute: 'JobSummary',
        priority: 'low',
      },
    ];
  };

  const loadNotificationSettings = async () => {
    try {
      // Try to load from service first
      let serviceSettings = {};
      if (IntegrationService && typeof IntegrationService.getNotificationSettings === 'function') {
        try {
          serviceSettings = await IntegrationService.getNotificationSettings();
        } catch (error) {
          console.warn('Service getNotificationSettings failed, using local data:', error);
        }
      }
      
      // Load from AsyncStorage
      const storedSettings = await AsyncStorage.getItem(`professional_${_profUserId}_notificationSettings`);
      const localSettings = storedSettings ? JSON.parse(storedSettings) : {};
      
      setNotificationSettings({
        ...notificationSettings,
        ...localSettings,
        ...serviceSettings,
      });
    } catch (error) {
      console.error('Error loading notification settings:', error);
      // Use default settings
    }
  };

  const updateAlertStatistics = (alertsList) => {
    const stats = {
      totalAlerts: alertsList.length,
      activeAlerts: alertsList.filter(alert => alert.enabled).length,
      jobsFound: alertsList.reduce((sum, alert) => sum + (alert.jobsFound || 0), 0),
      applicationsFromAlerts: alertsList.reduce((sum, alert) => sum + (alert.applicationsFromAlert || 0), 0),
      notificationsToday: notifications.filter(n => 
        new Date(n.timestamp).toDateString() === new Date().toDateString()
      ).length,
      unreadNotifications: notifications.filter(n => !n.isRead).length,
    };
    
    setAlertStats(stats);
  };

  const filterNotifications = (notificationsList, filter) => {
    let filtered = [];
    
    switch (filter) {
      case 'unread':
        filtered = notificationsList.filter(n => !n.isRead);
        break;
      case 'jobAlert':
        filtered = notificationsList.filter(n => n.type === 'jobAlert' || n.category === 'jobAlert');
        break;
      case 'all':
        filtered = notificationsList;
        break;
      default:
        filtered = notificationsList.filter(n => n.type === filter || n.category === filter);
        break;
    }
    
    setFilteredNotifications(filtered);
  };

  const handleCreateAlert = () => {
    setAlertForm({
      name: '',
      keywords: '',
      location: '',
      jobType: 'all',
      experienceLevel: 'all',
      salaryMin: '',
      salaryMax: '',
      industry: 'all',
      companySize: 'all',
      frequency: 'daily',
      enabled: true,
      emailNotifications: true,
      pushNotifications: true,
    });
    setShowCreateModal(true);
  };

  const handleEditAlert = (alert) => {
    setEditingAlert(alert);
    setAlertForm({
      name: alert.name,
      keywords: alert.keywords,
      location: alert.location,
      jobType: alert.jobType,
      experienceLevel: alert.experienceLevel,
      salaryMin: alert.salaryMin,
      salaryMax: alert.salaryMax,
      industry: alert.industry,
      companySize: alert.companySize,
      frequency: alert.frequency,
      enabled: alert.enabled,
      emailNotifications: alert.emailNotifications,
      pushNotifications: alert.pushNotifications,
    });
    setShowEditModal(true);
  };

  const handleSaveAlert = async () => {
    try {
      if (!alertForm.name.trim() || !alertForm.keywords.trim()) {
        Alert.alert('Error', 'Please fill in alert name and keywords.');
        return;
      }

      const alertData = {
        ...alertForm,
        id: editingAlert ? editingAlert.id : `alert_${Date.now()}`,
        createdAt: editingAlert ? editingAlert.createdAt : new Date().toISOString().split('T')[0],
        lastTriggered: editingAlert ? editingAlert.lastTriggered : null,
        jobsFound: editingAlert ? editingAlert.jobsFound : 0,
        newJobsToday: 0,
        notificationsGenerated: editingAlert ? editingAlert.notificationsGenerated : 0,
      };

      if (editingAlert) {
        // Update existing alert
        const updatedAlerts = alerts.map(alert => 
          alert.id === editingAlert.id ? alertData : alert
        );
        setAlerts(updatedAlerts);
        
        // Try to update via service
        if (IntegrationService && typeof IntegrationService.updateJobAlert === 'function') {
          try {
            await IntegrationService.updateJobAlert(alertData);
          } catch (error) {
            console.warn('Service updateJobAlert failed:', error);
          }
        }
        
        // Save to AsyncStorage
        await AsyncStorage.setItem(`professional_${_profUserId}_jobAlerts`, JSON.stringify(updatedAlerts));
        
        // Create notification for alert update
        await createNotification({
          type: 'system',
          title: 'Job Alert Updated',
          message: `Your job alert "${alertData.name}" has been updated successfully.`,
          priority: 'low',
        });
        
        Alert.alert('Success', 'Job alert updated successfully!');
      } else {
        // Create new alert
        const updatedAlerts = [...alerts, alertData];
        setAlerts(updatedAlerts);
        
        // Try to create via service
        if (IntegrationService && typeof IntegrationService.createJobAlert === 'function') {
          try {
            await IntegrationService.createJobAlert(alertData);
          } catch (error) {
            console.warn('Service createJobAlert failed:', error);
          }
        }
        
        // Save to AsyncStorage
        await AsyncStorage.setItem(`professional_${_profUserId}_jobAlerts`, JSON.stringify(updatedAlerts));
        
        // Create notification for new alert
        await createNotification({
          type: 'system',
          title: 'Job Alert Created',
          message: `Your new job alert "${alertData.name}" is now active and monitoring for opportunities.`,
          priority: 'medium',
        });
        
        Alert.alert('Success', 'Job alert created successfully!');
      }

      setShowCreateModal(false);
      setShowEditModal(false);
      setEditingAlert(null);
      
      // Reload notifications to show the new system notification
      await loadNotifications();
      
    } catch (error) {
      console.error('Error saving alert:', error);
      Alert.alert('Error', 'Failed to save job alert. Please try again.');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      Alert.alert(
        'Delete Job Alert',
        'Are you sure you want to delete this job alert? This will also remove all related notifications.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              const alertToDelete = alerts.find(a => a.id === alertId);
              const updatedAlerts = alerts.filter(alert => alert.id !== alertId);
              
              setAlerts(updatedAlerts);
              
              // Try to delete via service
              if (IntegrationService && typeof IntegrationService.deleteJobAlert === 'function') {
                try {
                  await IntegrationService.deleteJobAlert(alertId);
                } catch (error) {
                  console.warn('Service deleteJobAlert failed:', error);
                }
              }
              
              // Save to AsyncStorage
              await AsyncStorage.setItem(`professional_${_profUserId}_jobAlerts`, JSON.stringify(updatedAlerts));
              
              // Remove related notifications
              const updatedNotifications = notifications.filter(n => n.alertId !== alertId);
              setNotifications(updatedNotifications);
              await AsyncStorage.setItem(`professional_${_profUserId}_notifications`, JSON.stringify(updatedNotifications));
              
              // Create notification for alert deletion
              await createNotification({
                type: 'system',
                title: 'Job Alert Deleted',
                message: `Your job alert "${alertToDelete?.name}" has been deleted.`,
                priority: 'low',
              });
              
              Alert.alert('Success', 'Job alert deleted successfully!');
            }
          }
        ]
      );
    } catch (error) {
      console.error('Error deleting alert:', error);
      Alert.alert('Error', 'Failed to delete job alert. Please try again.');
    }
  };

  const handleToggleAlert = async (alertId, enabled) => {
    try {
      const updatedAlerts = alerts.map(alert => 
        alert.id === alertId ? { ...alert, enabled } : alert
      );
      setAlerts(updatedAlerts);
      
      const alert = updatedAlerts.find(a => a.id === alertId);
      
      // Try to update via service
      if (IntegrationService && typeof IntegrationService.updateJobAlert === 'function') {
        try {
          await IntegrationService.updateJobAlert(alert);
        } catch (error) {
          console.warn('Service updateJobAlert failed:', error);
        }
      }
      
      // Save to AsyncStorage
      await AsyncStorage.setItem(`professional_${_profUserId}_jobAlerts`, JSON.stringify(updatedAlerts));
      
      // Create notification for alert toggle
      await createNotification({
        type: 'system',
        title: `Job Alert ${enabled ? 'Enabled' : 'Disabled'}`,
        message: `Your job alert "${alert?.name}" has been ${enabled ? 'enabled' : 'disabled'}.`,
        priority: 'low',
      });
      
      Alert.alert('Success', `Job alert ${enabled ? 'enabled' : 'disabled'}!`);
      
    } catch (error) {
      console.error('Error toggling alert:', error);
      Alert.alert('Error', 'Failed to update job alert. Please try again.');
    }
  };

  const createNotification = async (notificationData) => {
    try {
      const notification = {
        id: `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        isRead: false,
        actionable: false,
        ...notificationData,
      };
      
      // Try to create via service
      if (IntegrationService && typeof IntegrationService.createNotification === 'function') {
        try {
          await IntegrationService.createNotification(notification);
        } catch (error) {
          console.warn('Service createNotification failed:', error);
        }
      }
      
      // Add to local state
      setNotifications(prev => [notification, ...prev]);
      
      // Save to AsyncStorage
      const updatedNotifications = [notification, ...notifications];
      await AsyncStorage.setItem(`professional_${_profUserId}_notifications`, JSON.stringify(updatedNotifications));
      
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      const updatedNotifications = notifications.map(notification =>
        notification.id === notificationId
          ? { ...notification, isRead: true }
          : notification
      );
      
      setNotifications(updatedNotifications);
      filterNotifications(updatedNotifications, notificationFilter);
      
      // Try to update via service
      if (IntegrationService && typeof IntegrationService.markNotificationAsRead === 'function') {
        try {
          await IntegrationService.markNotificationAsRead(notificationId);
        } catch (error) {
          console.warn('Service markNotificationAsRead failed:', error);
        }
      }
      
      // Save to AsyncStorage
      await AsyncStorage.setItem(`professional_${_profUserId}_notifications`, JSON.stringify(updatedNotifications));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationPress = async (notification) => {
    // Mark as read
    if (!notification.isRead) {
      await markNotificationAsRead(notification.id);
    }
    
    // Navigate to appropriate screen
    if (notification.actionable && notification.actionRoute) {
      navigation.navigate(notification.actionRoute, notification.actionParams || {});
    }
  };

  const toggleNotificationSetting = async (setting) => {
    try {
      const updatedSettings = {
        ...notificationSettings,
        [setting]: !notificationSettings[setting],
      };
      
      setNotificationSettings(updatedSettings);
      
      // Try to update via service
      if (IntegrationService && typeof IntegrationService.updateNotificationSettings === 'function') {
        try {
          await IntegrationService.updateNotificationSettings(updatedSettings);
        } catch (error) {
          console.warn('Service updateNotificationSettings failed:', error);
        }
      }
      
      // Save to AsyncStorage
      await AsyncStorage.setItem(`professional_${_profUserId}_notificationSettings`, JSON.stringify(updatedSettings));
    } catch (error) {
      console.error('Error updating notification settings:', error);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await initializeScreen();
    setRefreshing(false);
  };

  const renderHeader = () => (
    <LinearGradient
      colors={['#667eea', '#764ba2']}
      style={styles.header}
    >
      <View style={styles.headerContent}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        
        <Text style={styles.headerTitle}>Job Alerts & Notifications</Text>
        
        <TouchableOpacity
          style={styles.addButton}
          onPress={handleCreateAlert}
        >
          <Ionicons name="add" size={24} color="#ffffff" />
        </TouchableOpacity>
      </View>
      
      {/* Unread notification indicator */}
      {alertStats.unreadNotifications > 0 && (
        <View style={styles.unreadIndicatorContainer}>
          <Ionicons name="notifications" size={16} color="#fdb913" />
          <Text style={styles.unreadIndicatorText}>
            {alertStats.unreadNotifications} unread notifications
          </Text>
        </View>
      )}
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      {[
        { key: 'alerts', label: 'Alerts', icon: 'notifications', count: alertStats.activeAlerts },
        { key: 'notifications', label: 'Notifications', icon: 'chatbubble', count: alertStats.unreadNotifications },
        { key: 'settings', label: 'Settings', icon: 'settings', count: 0 },
      ].map((tab) => (
        <TouchableOpacity
          key={tab.key}
          style={[styles.tab, activeTab === tab.key && styles.activeTab]}
          onPress={() => setActiveTab(tab.key)}
        >
          <Ionicons
            name={tab.icon}
            size={20}
            color={activeTab === tab.key ? '#ffffff' : '#64748B'}
          />
          <Text style={[styles.tabText, activeTab === tab.key && styles.activeTabText]}>
            {tab.label}
          </Text>
          {tab.count > 0 && (
            <View style={styles.tabBadge}>
              <Text style={styles.tabBadgeText}>{tab.count}</Text>
            </View>
          )}
        </TouchableOpacity>
      ))}
    </View>
  );

  const renderStats = () => (
    <View style={styles.statsContainer}>
      <View style={styles.statCard}>
        <MaterialCommunityIcons name="bell" size={24} color="#667eea" />
        <Text style={styles.statNumber}>{alertStats.totalAlerts}</Text>
        <Text style={styles.statLabel}>Total Alerts</Text>
      </View>
      <View style={styles.statCard}>
        <MaterialCommunityIcons name="bell-check" size={24} color="#10B981" />
        <Text style={styles.statNumber}>{alertStats.activeAlerts}</Text>
        <Text style={styles.statLabel}>Active</Text>
      </View>
      <View style={styles.statCard}>
        <MaterialCommunityIcons name="briefcase-search" size={24} color="#F59E0B" />
        <Text style={styles.statNumber}>{alertStats.jobsFound}</Text>
        <Text style={styles.statLabel}>Jobs Found</Text>
      </View>
      <View style={styles.statCard}>
        <MaterialCommunityIcons name="message" size={24} color="#EF4444" />
        <Text style={styles.statNumber}>{alertStats.notificationsToday}</Text>
        <Text style={styles.statLabel}>Today</Text>
      </View>
    </View>
  );

  const renderAlertCard = ({ item: alert }) => {
    return (
      <View style={[styles.alertCard, !alert.enabled && styles.disabledCard]}>
        <View style={styles.alertHeader}>
          <View style={styles.alertInfo}>
            <Text style={styles.alertName}>{alert.name}</Text>
            <Text style={styles.alertKeywords}>{alert.keywords}</Text>
            <View style={styles.alertMeta}>
              <View style={styles.metaItem}>
                <Ionicons name="location-outline" size={14} color="#64748B" />
                <Text style={styles.metaText}>{alert.location}</Text>
              </View>
              <View style={styles.metaItem}>
                <Ionicons name="time-outline" size={14} color="#64748B" />
                <Text style={styles.metaText}>{alert.frequency}</Text>
              </View>
            </View>
          </View>
          <View style={styles.alertActions}>
            <Switch
              value={alert.enabled}
              onValueChange={(enabled) => handleToggleAlert(alert.id, enabled)}
              trackColor={{ false: '#E2E8F0', true: '#667eea' }}
              thumbColor={alert.enabled ? '#ffffff' : '#94A3B8'}
            />
          </View>
        </View>

        <View style={styles.alertDetails}>
          <View style={styles.alertTags}>
            <View style={styles.tag}>
              <Text style={styles.tagText}>{alert.jobType}</Text>
            </View>
            <View style={styles.tag}>
              <Text style={styles.tagText}>{alert.experienceLevel}</Text>
            </View>
            <View style={styles.tag}>
              <Text style={styles.tagText}>{alert.industry}</Text>
            </View>
          </View>

          {alert.salaryMin && alert.salaryMax && (
            <Text style={styles.salaryRange}>
              £{alert.salaryMin} - £{alert.salaryMax}
            </Text>
          )}

          <View style={styles.alertStats}>
            <View style={styles.alertStat}>
              <Text style={styles.alertStatNumber}>{alert.jobsFound}</Text>
              <Text style={styles.alertStatLabel}>Jobs Found</Text>
            </View>
            <View style={styles.alertStat}>
              <Text style={[styles.alertStatNumber, { color: '#10B981' }]}>{alert.newJobsToday}</Text>
              <Text style={styles.alertStatLabel}>New Today</Text>
            </View>
            <View style={styles.alertStat}>
              <Text style={[styles.alertStatNumber, { color: '#667eea' }]}>{alert.notificationsGenerated}</Text>
              <Text style={styles.alertStatLabel}>Notifications</Text>
            </View>
          </View>

          <View style={styles.notificationSettings}>
            <View style={styles.notificationItem}>
              <MaterialCommunityIcons 
                name="email" 
                size={16} 
                color={alert.emailNotifications ? '#10B981' : '#94A3B8'} 
              />
              <Text style={[styles.notificationText, { color: alert.emailNotifications ? '#10B981' : '#94A3B8' }]}>
                Email
              </Text>
            </View>
            <View style={styles.notificationItem}>
              <MaterialCommunityIcons 
                name="bell" 
                size={16} 
                color={alert.pushNotifications ? '#10B981' : '#94A3B8'} 
              />
              <Text style={[styles.notificationText, { color: alert.pushNotifications ? '#10B981' : '#94A3B8' }]}>
                Push
              </Text>
            </View>
          </View>

          <View style={styles.alertCardActions}>
            <TouchableOpacity
              style={styles.editButton}
              onPress={() => handleEditAlert(alert)}
            >
              <MaterialCommunityIcons name="pencil" size={16} color="#667eea" />
              <Text style={styles.editButtonText}>Edit</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.viewJobsButton}
              onPress={() => navigation.navigate('FindJobs', { alertId: alert.id })}
            >
              <MaterialCommunityIcons name="briefcase-search" size={16} color="#10B981" />
              <Text style={styles.viewJobsButtonText}>View Jobs</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.deleteButton}
              onPress={() => handleDeleteAlert(alert.id)}
            >
              <MaterialCommunityIcons name="delete" size={16} color="#EF4444" />
              <Text style={styles.deleteButtonText}>Delete</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const renderNotificationCard = ({ item: notification }) => {
    const getNotificationIcon = (type) => {
      const iconMap = {
        application: { name: 'document-text', color: '#0e4e96' },
        interview: { name: 'people', color: '#3b82f6' },
        message: { name: 'chatbubble', color: '#8b5cf6' },
        offer: { name: 'checkmark-circle', color: '#10b981' },
        jobAlert: { name: 'briefcase', color: '#f59e0b' },
        assessment: { name: 'clipboard', color: '#ec4899' },
        system: { name: 'information-circle', color: '#64748b' },
      };
      
      return iconMap[type] || { name: 'notifications', color: '#64748b' };
    };

    const formatTimestamp = (timestamp) => {
      const now = new Date();
      const notificationTime = new Date(timestamp);
      const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
      if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
      
      return notificationTime.toLocaleDateString();
    };

    return (
      <TouchableOpacity
        style={[
          styles.notificationCard,
          !notification.isRead && styles.unreadNotificationCard,
        ]}
        onPress={() => handleNotificationPress(notification)}
      >
        <View style={styles.notificationLeft}>
          <View style={styles.notificationIconContainer}>
            <View
              style={[
                styles.notificationIcon,
                { backgroundColor: getNotificationIcon(notification.type).color },
              ]}
            >
              <Ionicons
                name={getNotificationIcon(notification.type).name}
                size={20}
                color="#FFFFFF"
              />
            </View>
            {!notification.isRead && <View style={styles.unreadDot} />}
          </View>
        </View>

        <View style={styles.notificationContent}>
          <View style={styles.notificationHeader}>
            <Text style={styles.notificationTitle}>{notification.title}</Text>
            <Text style={styles.notificationTime}>
              {formatTimestamp(notification.timestamp || notification.date)}
            </Text>
          </View>

          <Text style={styles.notificationMessage}>{notification.message}</Text>

          {notification.actionable && (
            <View style={styles.notificationActions}>
              <TouchableOpacity
                style={styles.notificationAction}
                onPress={() => handleNotificationPress(notification)}
              >
                <Text style={styles.notificationActionText}>{notification.actionText}</Text>
                <Ionicons name="chevron-forward" size={16} color="#667eea" />
              </TouchableOpacity>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const renderNotificationFilters = () => (
    <View style={styles.filtersContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {[
          { key: 'all', label: 'All', icon: 'notifications' },
          { key: 'unread', label: 'Unread', icon: 'radio-button-off' },
          { key: 'jobAlert', label: 'Job Alerts', icon: 'briefcase' },
          { key: 'system', label: 'System', icon: 'information-circle' },
        ].map((filter) => (
          <TouchableOpacity
            key={filter.key}
            style={[
              styles.filterButton,
              notificationFilter === filter.key && styles.activeFilterButton
            ]}
            onPress={() => {
              setNotificationFilter(filter.key);
              filterNotifications(notifications, filter.key);
            }}
          >
            <Ionicons 
              name={filter.icon} 
              size={16} 
              color={notificationFilter === filter.key ? '#ffffff' : '#64748B'} 
            />
            <Text style={[
              styles.filterButtonText,
              notificationFilter === filter.key && styles.activeFilterButtonText
            ]}>
              {filter.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderSettingsSection = () => (
    <ScrollView style={styles.settingsContainer}>
      <View style={styles.settingsSection}>
        <Text style={styles.settingsSectionTitle}>Notification Types</Text>
        
        {[
          { key: 'applications', label: 'Application Updates', icon: 'document-text', color: '#0e4e96' },
          { key: 'interviews', label: 'Interview Notifications', icon: 'people', color: '#3b82f6' },
          { key: 'messages', label: 'Messages', icon: 'chatbubble', color: '#8b5cf6' },
          { key: 'jobAlerts', label: 'Job Alerts', icon: 'briefcase', color: '#f59e0b' },
          { key: 'offers', label: 'Job Offers', icon: 'checkmark-circle', color: '#10b981' },
          { key: 'assessments', label: 'Assessments', icon: 'clipboard', color: '#ec4899' },
          { key: 'networkUpdates', label: 'Network Updates', icon: 'people-circle', color: '#64748B' },
          { key: 'systemUpdates', label: 'System Updates', icon: 'information-circle', color: '#64748B' },
        ].map((setting) => (
          <View key={setting.key} style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Ionicons name={setting.icon} size={20} color={setting.color} />
              <Text style={styles.settingText}>{setting.label}</Text>
            </View>
            <Switch
              value={notificationSettings[setting.key]}
              onValueChange={() => toggleNotificationSetting(setting.key)}
              trackColor={{ false: '#E2E8F0', true: '#667eea' }}
              thumbColor={notificationSettings[setting.key] ? '#FFFFFF' : '#FFFFFF'}
            />
          </View>
        ))}
      </View>

      <View style={styles.settingsSection}>
        <Text style={styles.settingsSectionTitle}>Delivery Methods</Text>
        
        {[
          { key: 'pushNotifications', label: 'Push Notifications', icon: 'notifications', color: '#667eea' },
          { key: 'emailNotifications', label: 'Email Notifications', icon: 'mail', color: '#3b82f6' },
          { key: 'smsNotifications', label: 'SMS Notifications', icon: 'chatbox', color: '#10b981' },
        ].map((setting) => (
          <View key={setting.key} style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Ionicons name={setting.icon} size={20} color={setting.color} />
              <Text style={styles.settingText}>{setting.label}</Text>
            </View>
            <Switch
              value={notificationSettings[setting.key]}
              onValueChange={() => toggleNotificationSetting(setting.key)}
              trackColor={{ false: '#E2E8F0', true: '#667eea' }}
              thumbColor={notificationSettings[setting.key] ? '#FFFFFF' : '#FFFFFF'}
            />
          </View>
        ))}
      </View>
    </ScrollView>
  );

  const renderCreateEditModal = () => {
    const isEdit = showEditModal;
    const isVisible = showCreateModal || showEditModal;

    return (
      <Modal
        visible={isVisible}
        animationType="slide"
        presentationStyle="pageSheet"
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity
              onPress={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                setEditingAlert(null);
              }}
            >
              <Text style={styles.modalCancelText}>Cancel</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>
              {isEdit ? 'Edit Alert' : 'Create Job Alert'}
            </Text>
            <TouchableOpacity onPress={handleSaveAlert}>
              <Text style={styles.modalSaveText}>Save</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalContent} showsVerticalScrollIndicator={false}>
            {/* Alert Name */}
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Alert Name</Text>
              <TextInput
                style={styles.formInput}
                value={alertForm.name}
                onChangeText={(text) => setAlertForm(prev => ({ ...prev, name: text }))}
                placeholder="e.g., React Native Developer Jobs"
                placeholderTextColor="#94A3B8"
              />
            </View>

            {/* Keywords */}
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Keywords</Text>
              <TextInput
                style={styles.formInput}
                value={alertForm.keywords}
                onChangeText={(text) => setAlertForm(prev => ({ ...prev, keywords: text }))}
                placeholder="e.g., React Native, JavaScript, Mobile"
                placeholderTextColor="#94A3B8"
                multiline
              />
            </View>

            {/* Location */}
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Location</Text>
              <TextInput
                style={styles.formInput}
                value={alertForm.location}
                onChangeText={(text) => setAlertForm(prev => ({ ...prev, location: text }))}
                placeholder="e.g., London, UK or Remote"
                placeholderTextColor="#94A3B8"
              />
            </View>

            {/* Job Type */}
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Job Type</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {['all', 'full-time', 'part-time', 'contract', 'freelance', 'remote'].map((type) => (
                  <TouchableOpacity
                    key={type}
                    style={[
                      styles.optionButton,
                      alertForm.jobType === type && styles.selectedOption
                    ]}
                    onPress={() => setAlertForm(prev => ({ ...prev, jobType: type }))}
                  >
                    <Text style={[
                      styles.optionText,
                      alertForm.jobType === type && styles.selectedOptionText
                    ]}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            {/* Frequency */}
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Alert Frequency</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {['instant', 'daily', 'weekly'].map((freq) => (
                  <TouchableOpacity
                    key={freq}
                    style={[
                      styles.optionButton,
                      alertForm.frequency === freq && styles.selectedOption
                    ]}
                    onPress={() => setAlertForm(prev => ({ ...prev, frequency: freq }))}
                  >
                    <Text style={[
                      styles.optionText,
                      alertForm.frequency === freq && styles.selectedOptionText
                    ]}>
                      {freq.charAt(0).toUpperCase() + freq.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            {/* Notification Settings */}
            <View style={styles.formGroup}>
              <Text style={styles.formLabel}>Notifications</Text>
              <View style={styles.notificationToggles}>
                <View style={styles.notificationToggle}>
                  <Text style={styles.toggleLabel}>Email Notifications</Text>
                  <Switch
                    value={alertForm.emailNotifications}
                    onValueChange={(value) => setAlertForm(prev => ({ ...prev, emailNotifications: value }))}
                    trackColor={{ false: '#E2E8F0', true: '#667eea' }}
                    thumbColor={alertForm.emailNotifications ? '#ffffff' : '#94A3B8'}
                  />
                </View>
                <View style={styles.notificationToggle}>
                  <Text style={styles.toggleLabel}>Push Notifications</Text>
                  <Switch
                    value={alertForm.pushNotifications}
                    onValueChange={(value) => setAlertForm(prev => ({ ...prev, pushNotifications: value }))}
                    trackColor={{ false: '#E2E8F0', true: '#667eea' }}
                    thumbColor={alertForm.pushNotifications ? '#ffffff' : '#94A3B8'}
                  />
                </View>
              </View>
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#667eea" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#667eea" />
          <Text style={styles.loadingText}>Loading job alerts and notifications...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />
      
      {renderHeader()}
      {renderTabs()}
      
      <ScrollView 
        style={styles.container} 
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {renderStats()}
        
        {activeTab === 'alerts' && (
          <View style={styles.alertsContainer}>
            <FlatList
              data={alerts}
              renderItem={renderAlertCard}
              keyExtractor={(item) => item.id}
              showsVerticalScrollIndicator={false}
              scrollEnabled={false}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <MaterialCommunityIcons name="bell-plus" size={64} color="#94A3B8" />
                  <Text style={styles.emptyTitle}>No job alerts yet</Text>
                  <Text style={styles.emptyText}>
                    Create your first job alert to get notified about new opportunities
                  </Text>
                  <TouchableOpacity
                    style={styles.createFirstAlertButton}
                    onPress={handleCreateAlert}
                  >
                    <Text style={styles.createFirstAlertText}>Create Job Alert</Text>
                  </TouchableOpacity>
                </View>
              }
            />
          </View>
        )}

        {activeTab === 'notifications' && (
          <View style={styles.notificationsContainer}>
            {renderNotificationFilters()}
            <FlatList
              data={filteredNotifications}
              renderItem={renderNotificationCard}
              keyExtractor={(item) => item.id}
              showsVerticalScrollIndicator={false}
              scrollEnabled={false}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <MaterialCommunityIcons name="bell-outline" size={64} color="#94A3B8" />
                  <Text style={styles.emptyTitle}>No notifications</Text>
                  <Text style={styles.emptyText}>
                    You'll see job alert notifications and updates here
                  </Text>
                </View>
              }
            />
          </View>
        )}

        {activeTab === 'settings' && renderSettingsSection()}
      </ScrollView>

      {renderCreateEditModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#667eea',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
    fontWeight: '500',
  },
  header: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
  },
  addButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  unreadIndicatorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(253, 185, 19, 0.2)',
    borderRadius: 16,
  },
  unreadIndicatorText: {
    fontSize: 12,
    color: '#fdb913',
    fontWeight: '600',
    marginLeft: 6,
  },
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 20,
    marginHorizontal: 4,
  },
  activeTab: {
    backgroundColor: '#667eea',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
    marginLeft: 6,
  },
  activeTabText: {
    color: '#ffffff',
  },
  tabBadge: {
    backgroundColor: '#EF4444',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    marginLeft: 6,
  },
  tabBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#ffffff',
  },
  statsContainer: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    paddingVertical: 20,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  statNumber: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1E293B',
    marginTop: 8,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#64748B',
    fontWeight: '500',
    textAlign: 'center',
  },
  alertsContainer: {
    padding: 20,
  },
  notificationsContainer: {
    padding: 20,
  },
  filtersContainer: {
    marginBottom: 16,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 12,
  },
  activeFilterButton: {
    backgroundColor: '#667eea',
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
    marginLeft: 6,
  },
  activeFilterButtonText: {
    color: '#ffffff',
  },
  alertCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#667eea',
  },
  disabledCard: {
    opacity: 0.6,
    borderLeftColor: '#94A3B8',
  },
  alertHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  alertInfo: {
    flex: 1,
  },
  alertName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 4,
  },
  alertKeywords: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  alertMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  metaText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
  },
  alertActions: {
    alignItems: 'flex-end',
  },
  alertDetails: {
    marginTop: 8,
  },
  alertTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  tag: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginRight: 8,
    marginBottom: 8,
  },
  tagText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
  },
  salaryRange: {
    fontSize: 16,
    fontWeight: '700',
    color: '#059669',
    marginBottom: 12,
  },
  alertStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  alertStat: {
    alignItems: 'center',
  },
  alertStatNumber: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 2,
  },
  alertStatLabel: {
    fontSize: 10,
    color: '#64748B',
    fontWeight: '500',
  },
  notificationSettings: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  notificationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 20,
  },
  notificationText: {
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 6,
  },
  alertCardActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EEF2FF',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
  },
  editButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#667eea',
    marginLeft: 4,
  },
  viewJobsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#DCFCE7',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
  },
  viewJobsButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#10B981',
    marginLeft: 4,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEE2E2',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
  },
  deleteButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#EF4444',
    marginLeft: 4,
  },
  notificationCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    flexDirection: 'row',
  },
  unreadNotificationCard: {
    borderLeftWidth: 4,
    borderLeftColor: '#667eea',
  },
  notificationLeft: {
    marginRight: 12,
  },
  notificationIconContainer: {
    position: 'relative',
  },
  notificationIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  unreadDot: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#EF4444',
    borderWidth: 2,
    borderColor: '#ffffff',
  },
  notificationContent: {
    flex: 1,
  },
  notificationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  notificationTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    flex: 1,
    marginRight: 8,
  },
  notificationTime: {
    fontSize: 12,
    color: '#64748B',
  },
  notificationMessage: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 8,
  },
  notificationActions: {
    marginTop: 8,
  },
  notificationAction: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  notificationActionText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#667eea',
    marginRight: 4,
  },
  settingsContainer: {
    padding: 20,
  },
  settingsSection: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
  },
  settingsSectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 16,
  },
  settingItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  settingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  settingText: {
    fontSize: 16,
    color: '#1E293B',
    fontWeight: '500',
    marginLeft: 12,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1E293B',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  createFirstAlertButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 12,
  },
  createFirstAlertText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  modalCancelText: {
    fontSize: 16,
    color: '#64748B',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
  },
  modalSaveText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#667eea',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  formGroup: {
    marginBottom: 24,
  },
  formLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 8,
  },
  formInput: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#1E293B',
  },
  optionButton: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginRight: 12,
    marginBottom: 8,
  },
  selectedOption: {
    backgroundColor: '#667eea',
  },
  optionText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
  },
  selectedOptionText: {
    color: '#ffffff',
  },
  notificationToggles: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  notificationToggle: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  toggleLabel: {
    fontSize: 16,
    color: '#1E293B',
    fontWeight: '500',
  },
});
